'use client';

// library imports
import { PropsWithChildren } from 'react';

// shared imports
import { usePathname } from 'next/navigation';
import { cn } from '@fshr/shared-react';
import { Link, LinkIfFullyAuthenticated } from '@fshr/components';
import { useProfile } from '@profiles/lib/context/profile-context';

export interface ProfileSubNavLinkProps {
	href: string;
	requiresFullAuthentication?: boolean;
	showActive?: (pathname: string) => boolean;
}

export default function ProfileSubNavLink({
	href,
	requiresFullAuthentication = false,
	children,
	showActive,
}: PropsWithChildren<ProfileSubNavLinkProps>) {
	const pathname = usePathname();
	const { isFullyAuthenticated } = useProfile();

	const className = cn(
		[
			'h-[56px]',
			'border-y-[5px]',
			'border-transparent',
			'grid',
			'place-content-center',
			'uppercase',
			'whitespace-nowrap',
			'font-sans',
			'font-normal',
			'text-[12px]',
			'tracking-[3px]',
			'text-black',
			'transition-colors',
			'hover:text-neutral-900',
			'focus:text-neutral-900',
		],
		{
			'font-bold border-b-neutral-900': (showActive || (pathname => pathname === href))(pathname),
		}
	);

	const linkProps = {
		href,
		className,
		prefetch: false,
		children,
	};

	if (requiresFullAuthentication) {
		return (
			<LinkIfFullyAuthenticated
				isFullyAuthenticated={isFullyAuthenticated}
				intent="unstyled"
				{...linkProps}
			/>
		);
	}

	return <Link intent="unstyled" {...linkProps} />;
}
